"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  Vote,
  Sparkles,
  Target,
  Shield,
  Users,
  ArrowRight,
  Crown,
  Mic,
  Layers,
  TrendingUp,
  Building2,
  MapPin,
  Compass,
  BookOpen,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";
import { StepperBlock } from "@/lib/blocks/stepper-block";
import { BentoBlock } from "@/lib/blocks/bento-block";
import { CounterBlock } from "@/lib/blocks/counter-block";

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/30", ring: "ring-lime/20", pill: "glass-tinted-lime border-lime/20" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/30", ring: "ring-coral/20", pill: "glass-tinted-coral border-coral/20" },
};

const NINJA_PRINCIPLES = [
  { num: "01", title: "Complete Operational Silence", body: "Campaign activity leaves no trace. Intelligence assets operate without attribution. Content amplification emerges organically from the community." },
  { num: "02", title: "Compartmentalized Workflows", body: "Every operative sees only what they need, when they need it. No single individual outside unified command possesses the complete operational picture." },
  { num: "03", title: "Zero-Trace Communications", body: "All campaign communications operate through encrypted infrastructure with complete metadata elimination. No phone records, no email trails, no metadata." },
  { num: "04", title: "Unofficial Primacy", body: "The most influential content emerges from independent voices and perceived organic consensus, not from the campaign itself. Content appears discovered, not directed." },
  { num: "05", title: "Disappear-When-Done Architecture", body: "Infrastructure dissolves after mission completion. Accounts age out. Servers wipe encryption keys. The campaign&apos;s presence vanishes." },
];

const EVIDENCE = [
  { metric: "87", suffix: "%", label: "Booth-Level Accuracy", sub: "Ground-truth intelligence", accent: "red" },
  { metric: "15", suffix: "/18", label: "Campaign Victories", sub: "Verified outcomes", accent: "purple" },
  { metric: "900", suffix: "M+", label: "Voters Influenced", sub: "Across all engagements", accent: "cyan" },
  { metric: "73", suffix: "%", label: "Surprises Detected", sub: "Before they occur", accent: "emerald" },
];

const DIMENSIONS = [
  { num: "01", title: "Identity Demographics", body: "Caste, religion, age, gender, occupation, income bracket. The foundational characteristics that shape voter identity and influence voting behavior at the most basic level." },
  { num: "02", title: "Political Demographics", body: "Party affiliation history, voting pattern analysis, booth-level swing tendency. Maps the political DNA of each voter segment." },
  { num: "03", title: "Social Demographics", body: "Community networks, family linkage patterns, social group membership. Captures the social architecture through which political opinions form." },
  { num: "04", title: "Geo-Demographics", body: "Physical location mapping, polling station distance analysis, neighborhood characteristics. Grounds intelligence in physical space." },
  { num: "05", title: "Behavioral Demographics", body: "Media consumption patterns, issue sensitivity mapping, responsiveness triggers. Reveals how voters engage with political information." },
  { num: "06", title: "Psychographic Demographics", body: "Values, beliefs, aspirations, pain points, ideological orientation. Captures the emotional and philosophical foundations of political choice." },
];

const PHASES = [
  { num: "01", title: "Intelligence Foundation", body: "Every campaign begins with intelligence. Demography derivation, constituency proliferation, voter analysis, ground-truth validation, competitive intelligence gathering.", accent: "red" },
  { num: "02", title: "Strategy & Content Engineering", body: "Narrative conception, content identification, manifesto development, agenda setting, message calibration. The campaign blueprint emerges from verified voter intelligence.", accent: "purple" },
  { num: "03", title: "Operations & Coordination", body: "The War Room with its seven functional zones. Secure communications, field teams, amplification infrastructure. Unified command across all operational domains.", accent: "cyan" },
  { num: "04", title: "Execution & Management", body: "Content deployment through official and unofficial channels. Promotion, neutralization, demotion, transformation. The four strategic modes that shape voter perception.", accent: "emerald" },
];

export default function PoliticalStrategyPage() {
  return (
    <>
      <PageHero
        icon={Vote}
        overline="Political Strategy"
        overlineClassName="text-cyan"
        title={
          <>
            Engineering Electoral <span className="text-gradient-premium">Outcomes.</span>
          </>
        }
        subtitle="Elections are not won by charisma. They are engineered by intelligence, executed through technology, and validated by outcomes. The political strategy capability Dr. Jyoti Kush commands has produced an eighty-three-point-three percent success rate across sovereign-scale engagements where the margin for error is measured in national stability."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Expertise", href: "/expertise/" }, { label: "Political Strategy" }]}
        badge="Fifteen victories. Eighteen engagements."
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-5`}>
              <Sparkles className="size-3.5 text-cyan" />
              <span className="text-overline text-cyan">The Methodology</span>
            </div>
            <h2 className="text-display-md mb-5">
              Political engineering, not political consulting.
            </h2>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              Political strategy, as Dr. Jyoti Kush defines and operates it, is the systematic application of intelligence, technology, and ground-truth data to electoral outcomes. Not press releases, public rallies, and visible infrastructure. The engineering layer that operates beneath and beyond conventional politics.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              The political operations span two integrated divisions: Political Intelligence and Strategy, and Campaign Engineering and Operations. Intelligence informs strategy. Strategy drives execution. Execution generates new intelligence. The continuum never breaks.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            {EVIDENCE.map((e, i) => {
              const a = ACCENT[e.accent];
              return (
                <motion.div
                  key={e.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
                >
                  <div className={`text-display-sm font-bold ${a.text} leading-none mb-2`}>{e.metric}{e.suffix}</div>
                  <div className="text-caption font-bold uppercase tracking-wider mb-1">{e.label}</div>
                  <div className="text-caption text-muted-foreground">{e.sub}</div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.red.pill} mb-4`}>
            <Shield className="size-3.5 text-red" />
            <span className="text-overline text-red">The NINJA Doctrine</span>
          </div>
          <h2 className="text-display-md mb-4">Five non-negotiable principles.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Forged through years of operating in the world&apos;s most sensitive political environments. The doctrine governs every covert political operation — where exposure equals defeat.
          </p>
        </div>
        <div className="space-y-3">
          {NINJA_PRINCIPLES.map((p, i) => {
            const a = ACCENT[["red", "purple", "cyan", "emerald", "amber-gold"][i]];
            return (
              <motion.div
                key={p.num}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <div className="flex items-start gap-4">
                  <div className={`size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center shrink-0 shadow-lg`}>
                    <span className="text-body-md font-bold text-white">{p.num}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-heading-md mb-1.5 ${a.text}`}>{p.title}</h3>
                    <p className="text-body-sm text-muted-foreground leading-relaxed">{p.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-4`}>
            <Users className="size-3.5 text-purple" />
            <span className="text-overline text-purple">Demography Derivation</span>
          </div>
          <h2 className="text-display-md mb-4">Six dimensions of voter analysis.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The systematic analysis of voter populations across six distinct dimensions — processed through a six-stage pipeline that converts raw demographic data into booth-level micro-targeting intelligence.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {DIMENSIONS.map((d, i) => {
            const a = ACCENT[["red", "purple", "cyan", "emerald", "amber-gold", "denim"][i]];
            return (
              <motion.div
                key={d.num}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`group relative glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
              >
                <div className={`text-display-sm font-bold ${a.text} opacity-30 leading-none mb-3`}>{d.num}</div>
                <h3 className={`text-heading-md mb-2 ${a.text}`}>{d.title}</h3>
                <p className="text-caption text-muted-foreground leading-relaxed">{d.body}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.emerald.pill} mb-4`}>
            <Target className="size-3.5 text-emerald" />
            <span className="text-overline text-emerald">Campaign Engineering</span>
          </div>
          <h2 className="text-display-md mb-4">The four-phase operational engine.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Campaign engineering transforms intelligence into verified electoral outcomes. Every phase flows seamlessly into the next. The intelligence cycle continuously improves through closed-loop feedback.
          </p>
        </div>
        <div className="space-y-4">
          {PHASES.map((p, i) => {
            const a = ACCENT[p.accent];
            return (
              <motion.div
                key={p.num}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className={`group glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <div className="grid lg:grid-cols-[auto_1fr_auto] gap-5 items-center">
                  <div className={`size-14 rounded-2xl bg-gradient-sovereign flex items-center justify-center shadow-xl ring-2 ${a.ring}`}>
                    <span className="text-display-sm font-bold text-white">{p.num}</span>
                  </div>
                  <div>
                    <h3 className={`text-display-sm mb-2 ${a.text}`}>{p.title}</h3>
                    <p className="text-body-md text-muted-foreground leading-relaxed">{p.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.coral.pill} mb-4`}>
            <Compass className="size-3.5 text-coral" />
            <span className="text-overline text-coral">The Voter&apos;s Question</span>
          </div>
          <h2 className="text-display-md mb-4">What is in it for me?</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The fundamental question that every voter asks and that most campaigns fail to answer. Five principles of voter-centric campaigning that win when others lose.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {[
            { num: "01", title: "Issue-Based", body: "Every promise addresses a specific voter requirement identified through granular intelligence." },
            { num: "02", title: "People-Centric", body: "The voter is the hero of the story. The candidate is the instrument." },
            { num: "03", title: "Execution-Focused", body: "Concrete implementation plans rather than vague aspirations." },
            { num: "04", title: "Transparent", body: "Voters treated as intelligent adults capable of evaluating specific proposals." },
            { num: "05", title: "Good Governance", body: "Delivering on genuine promises is the ultimate electoral advantage." },
          ].map((t, i) => (
            <motion.div
              key={t.num}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-30px" }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              className="relative glass-card rounded-2xl p-5 bg-gradient-to-br from-coral/10 to-transparent border border-coral/20"
            >
              <div className="text-display-sm font-bold text-coral/30 leading-none mb-3">{t.num}</div>
              <h3 className="text-heading-md mb-2 text-coral">{t.title}</h3>
              <p className="text-caption text-muted-foreground leading-relaxed">{t.body}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20" id="political-ladder" ariaLabel="Political Strategy Ladder">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <TrendingUp className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">The Political Strategy Ladder</span>
          </div>
          <h2 className="text-display-md mb-4">Six stages from intelligence to outcome.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The signature vertical progression. Every rung builds on the one below. The political operator does not skip rungs. The system is the strength.
          </p>
        </div>
        <StepperBlock
          orientation="vertical"
          steps={[
            { num: "01", title: "Demography Derivation", body: "The six-dimension voter analysis pipeline. Identity, political, social, geo, behavioral, psychographic — processed through a six-stage pipeline that converts raw demographic data into booth-level micro-targeting intelligence.", icon: <Users className="size-4" />, accent: "red" },
            { num: "02", title: "Constituency Proliferation", body: "The five-layer intelligence matrix — ground truth, ground intelligence, predictive intelligence, behavioral intelligence, and competitive intelligence. Booth by booth, segment by segment, voter by voter.", icon: <MapPin className="size-4" />, accent: "purple" },
            { num: "03", title: "Intelligence Synthesis", body: "The signals are fused. The ground truth is verified. The pattern recognition runs. The five-layer intelligence matrix feeds the strategy layer with quantified confidence and predictive accuracy.", icon: <Target className="size-4" />, accent: "cyan" },
            { num: "04", title: "Campaign Engineering", body: "Four phases. Intelligence foundation, strategy and content, operations and coordination, execution and management. The campaign blueprint emerges from verified voter intelligence, not instinct.", icon: <Compass className="size-4" />, accent: "emerald" },
            { num: "05", title: "War Room Orchestration", body: "Seven functional zones coordinated in real time. Secure communications, field teams, amplification infrastructure. Unified command across all operational domains. Decision time compressed to under one hour.", icon: <Building2 className="size-4" />, accent: "amber-gold" },
            { num: "06", title: "Outcome Verification", body: "Every prediction reconciled against the actual result. The closed loop. The system that enters the next engagement is smarter than the one that entered the last.", icon: <Sparkles className="size-4" />, accent: "denim" },
          ]}
        />
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.red.pill} mb-4`}>
            <Shield className="size-3.5 text-red" />
            <span className="text-overline text-red">The Covert Operating Capabilities</span>
          </div>
          <h2 className="text-display-md mb-4">What sovereign campaigns demand.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Eight covert operating capabilities that distinguish sovereign-grade political operations from conventional campaign work. Each capability is non-negotiable when the stakes are national.
          </p>
        </div>
        <BentoBlock
          items={[
            { title: "Intelligence Fusion", body: "Fusing ground-truth intelligence with digital signals, demographic data, and competitive analysis. Eighty-seven percent booth-level accuracy.", icon: <Target className="size-5" />, accent: "red", span: "lg" },
            { title: "Ground Operations", body: "Booth-level teams in seventeen hundred-plus constituencies. Field operatives trained, equipped, and coordinated from the War Room.", icon: <Users className="size-5" />, accent: "purple", span: "sm" },
            { title: "Narrative Engineering", body: "Ninety-five to ninety-eight percent organic authenticity. Three hundred to five hundred percent penetration improvement. Content statistically indistinguishable from organic spread.", icon: <Sparkles className="size-5" />, accent: "cyan", span: "sm" },
            { title: "Sentiment Recovery", body: "Five recovery curves. The right curve selected per crisis. The wrong curve multiplies damage by orders of magnitude.", icon: <TrendingUp className="size-5" />, accent: "emerald", span: "sm" },
            { title: "Voter Database", body: "Eight hundred forty-seven-plus electoral cycles across seventy-three countries. The historical depth that gives predictions their accuracy.", icon: <BookOpen className="size-5" />, accent: "amber-gold", span: "sm" },
            { title: "Crisis Response", body: "Three hundred eighty-four to one thousand four hundred sixteen times faster than traditional approaches. Sub-five-minute response. Eighty-five to ninety-five percent success.", icon: <Shield className="size-5" />, accent: "denim", span: "wide" },
            { title: "Election Monitoring", body: "Real-time turnout tracking, anomaly detection, predictive modeling. The ability to detect and respond to irregularities as they emerge.", icon: <Vote className="size-5" />, accent: "coral", span: "sm" },
            { title: "Media Syndication", body: "One thousand-plus distribution points. Seventeen platforms in under five minutes. Cross-platform consistency maintained across fifty-plus languages.", icon: <Layers className="size-5" />, accent: "lime", span: "sm" },
          ]}
        />
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-4`}>
            <Mic className="size-3.5 text-purple" />
            <span className="text-overline text-purple">The Verified Outcomes</span>
          </div>
          <h2 className="text-display-md mb-4">The record of electoral performance.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Every metric traces to operational data from sovereign-scale deployments. The numbers do not represent projections. They represent outcomes that the record cannot dispute.
          </p>
        </div>
        <CounterBlock
          columns={4}
          stats={[
            { value: 87, suffix: "%", label: "Booth-Level Prediction", description: "Twenty-six points above digital-only methods", accent: "red" },
            { value: 15, suffix: "/18", label: "Campaign Victories", description: "Eighty-three-point-three percent success rate", accent: "purple" },
            { value: 900, suffix: "M+", label: "Voters Influenced", description: "Continental reach across all engagements", accent: "cyan" },
            { value: 73, suffix: "%", label: "Surprise Detection", description: "Adversary moves identified before materialization", accent: "emerald" },
            { value: 847, suffix: "+", label: "Electoral Cycles", description: "Historical depth across seventy-three countries", accent: "amber-gold" },
            { value: 95, suffix: "%", label: "Organic Authenticity", description: "Content statistically indistinguishable from organic spread", accent: "denim" },
          ]}
        />
      </Section>

      <Section background="default" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-cyan/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-purple/15 blur-3xl pointer-events-none" />
          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <Sparkles className="size-10 text-cyan mx-auto mb-4" />
            <h2 className="text-display-md mb-3">Inquire about political strategy.</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Engagements are by introduction only. The threshold for engagement is high. The standard of confidentiality is absolute. Every outcome verified.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/contact/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-gradient-sovereign text-white font-semibold shadow-lg hover:scale-105 transition-transform"
              >
                <Mic className="size-4" />
                Begin an Engagement
                <ArrowRight className="size-4" />
              </Link>
              <Link
                href="/expertise/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md glass font-semibold hover:scale-105 transition-transform"
              >
                <Layers className="size-4" />
                All Expertise Domains
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "Expertise Hub", description: "The complete map of fifteen expertise domains. One integrated operating system.", href: "/expertise/", icon: Vote, accent: "cyan" },
          { title: "Strategic Intelligence", description: "Seeing what others cannot. The intelligence architecture powering every engagement.", href: "/expertise/strategic-intelligence/", icon: Target, accent: "purple" },
          { title: "Perception & Reputation", description: "Engineering how the world sees you. Narrative deployment at continental scale.", href: "/expertise/perception-reputation/", icon: TrendingUp, accent: "emerald" },
          { title: "About Dr. Jyoti Kush", description: "The physician who became an operator. A decade of sovereign-scale leadership.", href: "/about/", icon: Crown, accent: "amber-gold" },
        ]}
      />
    </>
  );
}
